import Link from '@/routing/AppLink';
import type { ButtonHTMLAttributes } from 'react';

type VariantType = 'fill' | 'line';

type ShapeType = 'box' | 'round';

type ColorType = 'primary' | 'gray';

type SizeType = 'lg' | 'md' | 'sm';
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  type?: 'button' | 'submit' | 'reset';
  variant?: VariantType;
  shape?: ShapeType;
  color?: ColorType;
  size?: SizeType;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  href?: string;
  target?: '_self' | '_blank';
  blurOnFocus?: boolean;
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

/**
 * 버튼의 기본/모양/크기/배리언트 스타일은 globals.css 의 .btn* 에 있다.
 *
 * ModalFooter 등 caller 가 className 으로 w-full, h-[56px], rounded-none,
 * border-apps-coreProductDivider, text-apps-corePrimary01 을 넘겨 기본값을 덮어쓴다.
 * 기본 스타일을 유틸리티로 두면 모든 유틸리티가 !important 라 같은 속성끼리는
 * Tailwind 출력 순서로 승자가 정해져 caller 가 질 수 있다.
 * Emotion 때와 동일하게 caller 유틸리티가 이기도록 components 레이어 CSS 로 유지한다.
 * (base -> shape -> size -> variant 선언 순서도 원본 그대로다.)
 */
const SHAPE_CLASS: Record<ShapeType, string> = {
  box: 'btn--box',
  round: 'btn--round',
};

const SIZE_CLASS: Record<SizeType, string> = {
  lg: 'btn--lg',
  md: 'btn--md',
  sm: 'btn--sm',
};

const VARIANT_CLASS: Record<VariantType, Record<ColorType, string>> = {
  fill: {
    primary: 'btn--fill-primary',
    gray: 'btn--fill-gray',
  },
  line: {
    primary: 'btn--line-primary',
    gray: 'btn--line-gray',
  },
};

export default function Button({
  type = 'button',
  variant = 'fill',
  shape = 'box',
  color = 'primary',
  size = 'md',
  iconLeft,
  iconRight,
  href,
  target,
  children,
  disabled = false,
  blurOnFocus = true,
  onClick,
  className,
  ...props
}: ButtonProps) {
  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    // 사용자가 전달한 onClick 핸들러가 있다면 호출
    if (onClick) {
      onClick(event);
    }

    // blurOnFocus prop이 true일 경우 포커스 해제
    if (blurOnFocus) {
      event.currentTarget.blur();
    }
  };

  const buttonElement = (
    <button
      type={type}
      disabled={disabled}
      onClick={handleClick}
      className={`btn ${SHAPE_CLASS[shape]} ${SIZE_CLASS[size]} ${VARIANT_CLASS[variant][color]} ${className ?? ''}`}
      {...props}
    >
      {iconLeft && iconLeft}
      {children}
      {iconRight && iconRight}
    </button>
  );

  if (href) {
    return (
      <Link
        href={href}
        target={target}
        prefetch={false}
        aria-label={props['aria-label']}
        className={disabled ? 'pointer-events-none' : undefined}
      >
        {buttonElement}
      </Link>
    );
  }

  return buttonElement;
}
